<template>
  <div class="container admin-gpx-detail">
    <h1 class="hover" @click="$router.back()">← Voltar</h1>

    <div v-if="loading" class="muted">Carregando...</div>
    <div v-else-if="error" class="alert alert-danger">{{ error }}</div>

    <div v-else-if="upload">
      <div class="header-row">
        <div>
          <h2>{{ upload.original_filename }}</h2>
          <p class="sub">
            {{ upload.competition_name }} · Equipe #{{ upload.team_id }} ·
            {{ humanSize(upload.size_bytes) }}
          </p>
        </div>
        <div class="verdicts">
          <span :class="'badge big ' + verdictClass(upload.verdict)">
            {{ verdictLabel(upload.verdict) }}
          </span>
          <span v-if="upload.admin_review" :class="'badge big ' + (upload.admin_review === 'approved' ? 'ok' : 'bad')">
            {{ upload.admin_review === 'approved' ? 'aprovado' : 'rejeitado' }}
          </span>
        </div>
      </div>

      <div class="grid">
        <!-- Map column -->
        <div class="map-col">
          <mapbox-track
            :reference-geo="reference && reference.route"
            :athlete-geo="upload.geom"
            :start-point="reference && reference.start_point"
            :finish-point="reference && reference.finish_point"
            :start-label="startClock"
            :finish-label="finishClock"
          />
          <p v-if="!reference" class="muted small">
            Sem rota oficial cadastrada para esta prova — cobertura e desvio não foram calculados.
          </p>
          <a v-if="downloadUrl" :href="downloadUrl" class="link" target="_blank" rel="noopener">
            ⬇ Baixar GPX original
          </a>
        </div>

        <!-- Signals + actions column -->
        <div class="side-col">
          <section class="signals" v-if="signals">
            <h3>Sinais</h3>

            <div v-if="relay" class="relay-box" :class="relay.coverageOk ? 'gate-ok' : 'gate-fail'">
              <strong>🏃 Revezamento</strong> — cobertura combinada do time:
              <strong>{{ relay.combinedCoveragePercent != null ? relay.combinedCoveragePercent + '%' : 'n/d' }}</strong>
              <span v-if="relay.coverageOk != null">{{ relay.coverageOk ? '✓' : '✗' }}</span>
              <div class="small muted">{{ relay.legsCount }} perna(s) enviada(s) · mínimo {{ relay.minCoveragePercent }}%</div>
            </div>

            <ul class="gates">
              <li :class="gateClass(gates.coverageOk)">
                Cobertura da rota:
                <strong>{{ fmtPct(signals.coveragePercent) }}</strong>
                <small v-if="signals.isRelay" class="relay-note">(perna — não conta no veredito)</small>
                <small v-else-if="gates.coverageOk != null">{{ gates.coverageOk ? '✓' : '✗' }}</small>
              </li>
              <li :class="gateClass(gates.maxSpeedOk)">
                Velocidade máx. sustentada: <strong>{{ fmt(signals.maxSustainedKmh) }} km/h</strong>
                <small v-if="gates.maxSpeedOk != null">{{ gates.maxSpeedOk ? '✓' : '✗' }}</small>
              </li>
              <li :class="gateClass(gates.timeWindowOk)">
                Janela de tempo:
                <strong>{{ gates.timeWindowOk == null ? 'n/d' : (gates.timeWindowOk ? 'dentro' : 'fora') }}</strong>
                <small v-if="gates.timeWindowOk != null">{{ gates.timeWindowOk ? '✓' : '✗' }}</small>
              </li>
            </ul>

            <table class="info-table">
              <tr><td>Distância total</td><td>{{ fmt(signals.totalDistanceKm) }} km</td></tr>
              <tr><td>Duração</td><td>{{ fmtDuration(signals.totalDurationSec) }}</td></tr>
              <tr><td>Vel. média</td><td>{{ fmt(signals.avgSpeedKmh) }} km/h</td></tr>
              <tr><td>Vel. instantânea máx.</td><td>{{ fmt(signals.maxInstantSpeedKmh) }} km/h</td></tr>
              <tr><td>Ganho de elevação</td><td>{{ signals.elevationGainM }} m</td></tr>
              <tr><td>Desvio máx. da rota</td><td>{{ signals.maxDeviationM != null ? signals.maxDeviationM + ' m' : '—' }}</td></tr>
              <tr :class="{ 'warn-row': signals.offRoutePercent > 10 }">
                <td>Pontos fora da rota</td>
                <td>{{ signals.offRoutePercent != null ? signals.offRoutePercent + '% (' + signals.offRoutePointsCount + ')' : '—' }}</td>
              </tr>
              <tr><td>Gaps de GPS</td><td>{{ signals.gapsCount }}</td></tr>
              <tr><td>Início no raio</td><td>{{ boolLabel(signals.startInRadius) }}</td></tr>
              <tr><td>Fim no raio</td><td>{{ boolLabel(signals.finishInRadius) }}</td></tr>
            </table>

            <div v-if="taggedMembers.length" class="tagged">
              <strong>Atletas marcados:</strong>
              {{ taggedMembers.map(m => m.name).join(', ') }}
            </div>
          </section>

          <section v-else class="muted">
            Ainda não processado ({{ statusLabel(upload.status) }}).
            <span v-if="upload.error_message" class="error">{{ upload.error_message }}</span>
          </section>

          <!-- Threshold override + reprocess -->
          <section class="thresholds">
            <h3>Ajustar limites e reprocessar</h3>
            <p class="small muted">Sobrescreve apenas este upload. Em branco = herda da prova.</p>
            <label>Cobertura mínima (%)
              <input type="number" v-model.number="override.minCoveragePercent" placeholder="herdado" />
            </label>
            <label>Vel. máx. corrida (km/h)
              <input type="number" v-model.number="override.maxRunningKmh" placeholder="herdado" />
            </label>
            <label>Buffer da rota (m)
              <input type="number" v-model.number="override.routeBufferM" placeholder="herdado" />
            </label>
            <label>Tolerância janela (min)
              <input type="number" v-model.number="override.raceWindowGraceMin" placeholder="herdado" />
            </label>
            <button class="btn btn-secondary" :disabled="busy" @click="saveOverrideAndReprocess">
              Salvar e reprocessar
            </button>
          </section>

          <!-- Approve / reject -->
          <section class="review-note-info">
            <p class="small muted">
              A decisão de aprovar/rejeitar é feita no nível da equipe.
              <router-link v-if="teamLink" :to="teamLink">← voltar para a equipe</router-link>
            </p>
            <p v-if="upload.admin_review" class="prev-note">
              Decisão atual da equipe:
              <strong>{{ upload.admin_review === 'approved' ? 'aprovada' : 'rejeitada' }}</strong>
              <span v-if="upload.admin_review_note"> — {{ upload.admin_review_note }}</span>
            </p>
          </section>

          <section class="danger-zone">
            <button class="btn btn-outline-danger" :disabled="busy" @click="doDelete">
              Excluir upload (soft delete + remover do R2)
            </button>
            <button class="btn btn-light" :disabled="busy" @click="reprocess">
              Reprocessar
            </button>
          </section>

          <p v-if="actionMsg" class="action-msg">{{ actionMsg }}</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/api';
import MapboxTrack from '@/components/Map/mapbox-track.vue';

export default {
  name: 'admin-gpx-detail',
  components: { MapboxTrack },

  data() {
    return {
      loading: true,
      busy: false,
      error: null,
      upload: null,
      reference: null,
      taggedMembers: [],
      downloadUrl: null,
      relay: null,
      reviewNote: '',
      override: {
        minCoveragePercent: null,
        maxRunningKmh: null,
        routeBufferM: null,
        raceWindowGraceMin: null,
      },
      actionMsg: null,
    };
  },

  computed: {
    id() {
      return parseInt(this.$route.params.id);
    },
    signals() {
      return this.upload && this.upload.signals;
    },
    gates() {
      return (this.signals && this.signals.gates) || {};
    },
    teamLink() {
      if (!this.upload || !this.upload.team_id || !this.upload.competition_id) return null;
      return '/admin/gpx/team/' + this.upload.team_id + '/' + this.upload.competition_id;
    },
    // Horário em que a trace entra/sai da rota — exibido nos pinos do mapa.
    startClock() {
      const s = this.signals;
      const ms = s && (s.onRouteFirstTimestamp != null ? s.onRouteFirstTimestamp : s.firstTimestamp);
      return ms != null ? this.clockH(ms) : null;
    },
    finishClock() {
      const s = this.signals;
      const ms = s && (s.onRouteLastTimestamp != null ? s.onRouteLastTimestamp : s.lastTimestamp);
      return ms != null ? this.clockH(ms) : null;
    },
  },

  async created() {
    await this.load();
  },

  methods: {
    async load() {
      this.loading = true;
      this.error = null;
      try {
        const res = await api.getJSONAsync('gpxAdminGet', { id: this.id });
        if (!res || res.error) {
          this.error = (res && res.message) || 'Erro ao carregar';
          return;
        }
        this.upload = res.upload;
        this.reference = res.reference;
        this.taggedMembers = res.taggedMembers || [];
        this.downloadUrl = res.downloadUrl;
        this.relay = res.relay || null;
        // Pre-fill override fields if present.
        const ov = this.upload.thresholds_override || {};
        for (const k of Object.keys(this.override)) {
          if (ov[k] != null) this.override[k] = ov[k];
        }
      } finally {
        this.loading = false;
      }
    },

    buildOverride() {
      const out = {};
      for (const k of Object.keys(this.override)) {
        const v = this.override[k];
        if (v != null && v !== '' && isFinite(v)) out[k] = Number(v);
      }
      return out;
    },

    async saveOverrideAndReprocess() {
      this.busy = true;
      this.actionMsg = null;
      try {
        const res = await api.postJSONAsync('gpxAdminReviewAction', {
          gpxUploadId: this.id,
          action: this.upload.admin_review || 'clear',
          note: this.upload.admin_review_note || null,
          thresholdsOverride: this.buildOverride(),
          reprocess: true,
        });
        if (res && res.error) throw new Error(res.message);
        this.actionMsg = 'Limites salvos. Reprocessando...';
        setTimeout(() => this.load(), 2500);
      } catch (e) {
        this.actionMsg = 'Erro: ' + (e.message || e);
      } finally {
        this.busy = false;
      }
    },

    async review(action) {
      this.busy = true;
      this.actionMsg = null;
      try {
        const res = await api.postJSONAsync('gpxAdminReviewAction', {
          gpxUploadId: this.id,
          action,
          note: this.reviewNote || null,
        });
        if (res && res.error) throw new Error(res.message);
        this.actionMsg = action === 'rejected'
          ? 'Rejeitado. E-mail enviado à equipe.'
          : (action === 'clear' ? 'Revisão limpa.' : 'Aprovado.');
        await this.load();
      } catch (e) {
        this.actionMsg = 'Erro: ' + (e.message || e);
      } finally {
        this.busy = false;
      }
    },

    async reprocess() {
      this.busy = true;
      try {
        await api.postJSONAsync('gpxAdminReprocess', { id: this.id });
        this.actionMsg = 'Reprocessando...';
        setTimeout(() => this.load(), 2500);
      } finally {
        this.busy = false;
      }
    },

    async doDelete() {
      if (!confirm('Excluir este upload? O arquivo será removido do R2 (metadados ficam para auditoria).')) return;
      this.busy = true;
      try {
        const res = await api.deleteJSON('gpxAdminDelete', { id: this.id });
        if (res && res.data && res.data.deleted) {
          this.$router.push(this.teamLink || '/admin/events');
        } else {
          this.actionMsg = 'Não foi possível excluir.';
        }
      } finally {
        this.busy = false;
      }
    },

    // --- formatters ---
    humanSize(n) {
      n = Number(n) || 0;
      if (n < 1024 * 1024) return (n / 1024).toFixed(0) + ' KB';
      return (n / 1024 / 1024).toFixed(1) + ' MB';
    },
    fmt(v) { return v != null ? Number(v).toFixed(2) : '—'; },
    clockH(ms) {
      const d = new Date(ms);
      const p = (n) => String(n).padStart(2, '0');
      return p(d.getHours()) + ':' + p(d.getMinutes()) + 'h';
    },
    fmtPct(v) { return v != null ? v + '%' : '—'; },
    fmtDuration(s) {
      if (s == null) return '—';
      const h = Math.floor(s / 3600);
      const m = Math.floor((s % 3600) / 60);
      return `${h}h ${m}min`;
    },
    boolLabel(v) { return v == null ? '—' : (v ? 'sim' : 'não'); },
    gateClass(v) { return v == null ? 'gate-na' : (v ? 'gate-ok' : 'gate-fail'); },
    verdictLabel(v) { return ({ ok: 'OK', warning: 'atenção', suspect: 'suspeito' })[v] || 'pendente'; },
    verdictClass(v) { return ({ ok: 'ok', warning: 'wait', suspect: 'bad' })[v] || 'neutral'; },
    statusLabel(s) { return ({ uploaded: 'na fila', processing: 'processando', processed: 'processado', failed: 'falhou' })[s] || s; },
  },
};
</script>

<style scoped lang="stylus">
.admin-gpx-detail
  padding: 1.5em 0

.header-row
  display: flex
  justify-content: space-between
  align-items: flex-start
  margin-bottom: 1em

.sub
  color: #777
  margin: 0.2em 0 0

.verdicts
  display: flex
  gap: 0.5em

.grid
  display: grid
  grid-template-columns: 1.6fr 1fr
  gap: 1.5em

@media (max-width: 900px)
  .grid
    grid-template-columns: 1fr

.badge
  display: inline-block
  padding: 0.15em 0.5em
  border-radius: 4px
  font-size: 0.82em

.badge.big
  padding: 0.35em 0.9em
  font-size: 0.95em

.badge.ok
  background: #d1e7dd
  color: #0f5132
.badge.bad
  background: #f8d7da
  color: #842029
.badge.wait
  background: #fff3cd
  color: #664d03
.badge.neutral
  background: rgba(0,0,0,0.06)
  color: #555

.signals h3, .thresholds h3, .review h3
  margin: 0 0 0.6em

.gates
  list-style: none
  padding: 0
  margin: 0 0 1em

.gates li
  padding: 0.5em 0.7em
  border-radius: 6px
  margin-bottom: 0.4em
  font-size: 0.9em

.gate-ok
  background: #d1e7dd
.gate-fail
  background: #f8d7da
.gate-na
  background: rgba(0,0,0,0.04)

.gates small
  float: right
  font-weight: 700

.relay-note
  float: none !important
  font-weight: 400 !important
  color: #777
  font-size: 0.8em
  margin-left: 0.4em

.relay-box
  padding: 0.7em 0.9em
  border-radius: 6px
  margin-bottom: 0.8em
  font-size: 0.9em

.warn-row td
  background: #fff3cd

.info-table
  width: 100%
  border-collapse: collapse
  font-size: 0.88em

.info-table td
  padding: 0.35em 0.5em
  border-bottom: 1px solid rgba(0,0,0,0.06)

.info-table td:last-child
  text-align: right
  font-weight: 600

.tagged
  margin-top: 0.8em
  font-size: 0.88em

.thresholds, .review, .danger-zone
  margin-top: 1.5em
  padding-top: 1em
  border-top: 1px solid rgba(0,0,0,0.08)

.thresholds label
  display: flex
  justify-content: space-between
  align-items: center
  font-size: 0.85em
  margin-bottom: 0.5em
  gap: 0.6em

.thresholds input
  width: 90px
  padding: 0.3em
  border: 1px solid #ccc
  border-radius: 4px

.review textarea
  width: 100%
  min-height: 60px
  padding: 0.5em
  border: 1px solid #ccc
  border-radius: 4px
  margin-bottom: 0.6em

.review-actions, .danger-zone
  display: flex
  gap: 0.6em
  flex-wrap: wrap

.btn
  padding: 0.45em 1em
  border-radius: 5px
  border: none
  cursor: pointer
  font-size: 0.9em

.btn-success
  background: #2ca25f
  color: white
.btn-danger
  background: #de2d26
  color: white
.btn-secondary
  background: #17a2b8
  color: white
  margin-top: 0.4em
.btn-light
  background: #eee
.btn-outline-danger
  background: white
  border: 1px solid #de2d26
  color: #de2d26

.prev-note
  font-size: 0.82em
  color: #777
  margin-top: 0.5em

.action-msg
  margin-top: 1em
  padding: 0.6em
  background: rgba(23,162,184,0.1)
  border-radius: 5px
  font-size: 0.9em

.small
  font-size: 0.82em
.muted
  color: #999
.error
  color: #842029
.link
  color: #17a2b8
  display: inline-block
  margin-top: 0.6em
.hover
  cursor: pointer
</style>
